import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { dateFormatter } from "../../core/utils";
import { UserResponse } from "../../redux/features/user-slice";

interface Props {
  type: string;
  data: any;
  setData: (value: any) => void;
  hideReason?: boolean;
  user: UserResponse | null;
}

export default function InfoDialog(props: Props) {
  const { type, data, setData, hideReason = false, user } = props;

  const cancelButtonRef = useRef(null);

  const handleClose = () => {
    setData(null);
  };

  const customerEmail = user?.customer?.email || "";
  const customerName = user?.customer?.name || "N/A";

  if (!data) return null;

  return (
    <Transition.Root show={data !== null} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 rounded-lg"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden bg-background-paper text-left shadow-xl transition-all sm:my-4 sm:w-full sm:max-w-lg rounded-lg">
                <div className="p-6">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={handleClose}
                    >
                      <span className="sr-only">Close</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="h-6 w-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <Dialog.Title className="text-base font-semibold leading-6 text-gray-900 mt-4">
                    {`${data?.FullName || "N/A"} - ${
                      data?.OrganizationUnitName || "N/A"
                    }`}
                  </Dialog.Title>
                  {
                    <p className="text-base text-gray-900 mt-4">
                      {`Customer: ${customerName || "N/A"}`}
                    </p>
                  }
                  <div className="mt-4">
                    <p className="text-base text-gray-900">
                      {`From: ${
                        dateFormatter(data?.FromDate, "dd/MM/yyyy hh:mm a") ||
                        "N/A"
                      } - To: ${
                        dateFormatter(data?.ToDate, "dd/MM/yyyy hh:mm a") ||
                        "N/A"
                      }`}
                    </p>
                    {!hideReason && (
                      <p className="text-base text-gray-900 mt-4">
                        {`Reason: ${data?.Reason || "Not specified"}`}
                      </p>
                    )}
                    {type === "Attendance" && (
                      <p className="text-base text-gray-900 mt-4">
                        {`Attendance Type: ${
                          data?.AttendanceTypeName_EN || "N/A"
                        }`}
                      </p>
                    )}
                    {customerEmail && (
                      <div className="mt-4">
                        <a
                          href={`mailto:${customerEmail}`}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Inform {customerName}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
